import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import AppShell from "./AppShell";
import axios from "axios";
import { Plus, Trash2, Send, Save, ArrowRight, FileText } from "lucide-react";

const CURRENCIES = ["USD","EUR","GBP","JPY","AUD","CAD","CHF","CNY","INR","MXN","BRL","KRW","SEK","NOK","DKK","SGD","HKD","NZD","ZAR","AED"];

const EstimateBuilder = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [estimate, setEstimate] = useState({
    client_id: "", estimate_number: "",
    issue_date: new Date().toISOString().split("T")[0],
    valid_until: new Date(Date.now() + 30*24*60*60*1000).toISOString().split("T")[0],
    items: [{ description: "", quantity: 1, rate: 0, amount: 0 }],
    notes: "", tax_rate: 0, discount: 0, currency: "USD",
    taxes: [], country_code: ""
  });
  const [clients, setClients] = useState([]);
  const [taxConfigs, setTaxConfigs] = useState({});
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    fetchClients();
    fetchTaxConfigs();
    if (isEditing) fetchEstimate();
    else if (user?.default_country) loadCountryTaxes(user.default_country);
  }, [id]);

  const fetchClients = async () => { try { const { data } = await axios.get(`${API}/clients`); setClients(data); } catch (e) {} };
  const fetchTaxConfigs = async () => { try { const { data } = await axios.get(`${API}/tax-configs`); setTaxConfigs(data); } catch (e) {} };

  const fetchEstimate = async () => {
    try {
      const { data } = await axios.get(`${API}/estimates/${id}`);
      setEstimate({
        client_id: data.client_id, estimate_number: data.estimate_number,
        issue_date: data.issue_date, valid_until: data.valid_until,
        items: data.items?.length > 0 ? data.items : [{ description: "", quantity: 1, rate: 0, amount: 0 }],
        notes: data.notes || "", tax_rate: data.tax_rate || 0, discount: data.discount || 0,
        currency: data.currency || "USD", taxes: data.taxes || [], country_code: data.country_code || ""
      });
      setSelectedClient(data.client);
    } catch (err) { navigate("/estimates"); }
  };

  const loadCountryTaxes = (code) => {
    if (!code) { setEstimate(prev => ({ ...prev, taxes: [], country_code: "" })); return; }
    const config = taxConfigs[code];
    if (config) setEstimate(prev => ({ ...prev, taxes: config.taxes.map(t => ({ label: t.label, rate: t.rate, amount: 0 })), country_code: code, tax_rate: 0 }));
  };

  const updateItem = (index, field, value) => {
    const items = [...estimate.items];
    items[index] = { ...items[index], [field]: value };
    if (field === "quantity" || field === "rate") items[index].amount = (parseFloat(items[index].quantity) || 0) * (parseFloat(items[index].rate) || 0);
    setEstimate(prev => ({ ...prev, items }));
  };

  const addItem = () => setEstimate(prev => ({ ...prev, items: [...prev.items, { description: "", quantity: 1, rate: 0, amount: 0 }] }));
  const removeItem = (i) => { if (estimate.items.length > 1) setEstimate(prev => ({ ...prev, items: prev.items.filter((_, j) => j !== i) })); };

  const subtotal = estimate.items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const taxAmount = estimate.taxes?.length > 0 ? estimate.taxes.reduce((s, t) => s + subtotal * ((parseFloat(t.rate) || 0) / 100), 0) : subtotal * ((parseFloat(estimate.tax_rate) || 0) / 100);
  const total = subtotal + taxAmount - (parseFloat(estimate.discount) || 0);

  const formatCurrency = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: estimate.currency }).format(v || 0);

  const handleSave = async (status = "draft") => {
    if (!estimate.client_id) { alert("Please select a client"); return; }
    if (!estimate.items.some(i => i.description)) { alert("Add at least one line item"); return; }
    setSaving(true);
    try {
      const payload = {
        ...estimate, status,
        items: estimate.items.filter(i => i.description),
        taxes: estimate.taxes?.length > 0 ? estimate.taxes.map(t => ({ label: t.label, rate: parseFloat(t.rate) || 0, amount: subtotal * ((parseFloat(t.rate) || 0) / 100) })) : undefined
      };
      if (isEditing) {
        await axios.put(`${API}/estimates/${id}`, payload);
      } else {
        const { data } = await axios.post(`${API}/estimates`, payload);
        navigate(`/estimates/${data.id}/edit`, { replace: true });
      }
    } catch (err) { alert(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleSend = async () => {
    await handleSave("sent");
    if (!isEditing) return;
    setSending(true);
    try {
      const est = (await axios.get(`${API}/estimates/${id}`)).data;
      await axios.post(`${API}/estimates/${id}/send-email`, {
        share_url: `${window.location.origin}/estimate/${est.share_token}`,
        subject: `Estimate ${est.estimate_number}`,
      });
      alert("Estimate sent!");
      navigate("/estimates");
    } catch (err) { alert(err.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };

  const handleConvert = async () => {
    try {
      const { data } = await axios.post(`${API}/estimates/${id}/convert`);
      navigate(`/invoices/${data.id}`);
    } catch (err) { alert(err.response?.data?.detail || "Failed to convert"); }
  };

  return (
    <AppShell active="estimates">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-[22px] sm:text-[28px] tracking-[-0.03em] text-[#0A0A0A]">
            {isEditing ? "Edit Estimate" : "New Estimate"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {isEditing && estimate.status === "accepted" && (
            <button onClick={handleConvert} className="flex items-center gap-1.5 px-3 py-2 bg-[#059669] text-white text-[13px] font-semibold hover:bg-[#047857]" data-testid="convert-btn">
              <ArrowRight className="w-4 h-4" /> Convert to invoice
            </button>
          )}
          <button onClick={() => handleSave("draft")} disabled={saving} className="btn-outline text-[13px] px-4 py-2 flex items-center gap-1.5" data-testid="save-draft-btn">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save draft"}
          </button>
          {isEditing && (
            <button onClick={handleSend} disabled={sending} className="btn-primary text-[13px] px-4 py-2 flex items-center gap-1.5" data-testid="send-estimate-btn">
              <Send className="w-4 h-4" /> {sending ? "Sending..." : "Send"}
            </button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: Items */}
        <div className="lg:col-span-2 space-y-4">
          {/* Client + Meta */}
          <div className="bg-white border border-[#E5E7EB] p-4 sm:p-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">Client</label>
                <select value={estimate.client_id} onChange={e => { setEstimate(prev => ({ ...prev, client_id: e.target.value })); setSelectedClient(clients.find(c => c.id === e.target.value)); }} className="input text-[14px]" data-testid="estimate-client">
                  <option value="">Select client</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">Estimate #</label>
                <input type="text" value={estimate.estimate_number} onChange={e => setEstimate(prev => ({ ...prev, estimate_number: e.target.value }))} className="input text-[14px] font-mono" placeholder="Auto-generated" data-testid="estimate-number" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">Issue date</label>
                <input type="date" value={estimate.issue_date} onChange={e => setEstimate(prev => ({ ...prev, issue_date: e.target.value }))} className="input text-[14px]" data-testid="estimate-issue-date" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">Valid until</label>
                <input type="date" value={estimate.valid_until} onChange={e => setEstimate(prev => ({ ...prev, valid_until: e.target.value }))} className="input text-[14px]" data-testid="estimate-valid-until" />
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="bg-white border border-[#E5E7EB] p-4 sm:p-5">
            <h2 className="font-semibold text-[15px] text-[#0A0A0A] mb-4">Line Items</h2>
            <div className="space-y-3">
              {estimate.items.map((item, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`item-row-${i}`}>
                  <input type="text" value={item.description} onChange={e => updateItem(i, "description", e.target.value)} placeholder="Description" className="col-span-5 input text-[13px]" data-testid={`item-desc-${i}`} />
                  <input type="number" value={item.quantity} onChange={e => updateItem(i, "quantity", e.target.value)} className="col-span-2 input text-[13px] text-right font-mono" min="0" data-testid={`item-qty-${i}`} />
                  <input type="number" value={item.rate} onChange={e => updateItem(i, "rate", e.target.value)} className="col-span-2 input text-[13px] text-right font-mono" min="0" step="0.01" data-testid={`item-rate-${i}`} />
                  <span className="col-span-2 text-right font-mono font-semibold text-[13px] text-[#0A0A0A]">{formatCurrency(item.amount)}</span>
                  <button onClick={() => removeItem(i)} className="col-span-1 p-1.5 text-[#9CA3AF] hover:text-[#EF4444] justify-self-center" data-testid={`item-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <button onClick={addItem} className="mt-3 flex items-center gap-1.5 text-[13px] font-semibold text-[#1A3DFF] hover:underline" data-testid="add-item-btn"><Plus className="w-4 h-4" /> Add line item</button>
          </div>

          {/* Notes */}
          <div className="bg-white border border-[#E5E7EB] p-4 sm:p-5">
            <label className="block text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">Notes</label>
            <textarea value={estimate.notes} onChange={e => setEstimate(prev => ({ ...prev, notes: e.target.value }))} rows={3} className="input text-[14px]" placeholder="Terms, conditions, or additional notes..." data-testid="estimate-notes" />
          </div>
        </div>

        {/* Right: Summary */}
        <div className="space-y-4">
          <div className="bg-white border border-[#E5E7EB] p-4 sm:p-5 sticky top-20">
            <h2 className="font-semibold text-[15px] text-[#0A0A0A] mb-4">Summary</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">Currency</label>
                <select value={estimate.currency} onChange={e => setEstimate(prev => ({ ...prev, currency: e.target.value }))} className="input text-[13px]" data-testid="estimate-currency">
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex justify-between"><span className="text-[14px] text-[#6B7280]">Subtotal</span><span className="font-mono font-semibold text-[14px]">{formatCurrency(subtotal)}</span></div>

              {/* Tax Region */}
              <div>
                <label className="block text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">Tax Region</label>
                <select value={estimate.country_code || ""} onChange={e => { const code = e.target.value; if (!code) { setEstimate(prev => ({ ...prev, taxes: [], country_code: "", tax_rate: 0 })); return; } const config = taxConfigs[code]; if (config) setEstimate(prev => ({ ...prev, taxes: config.taxes.map(t => ({ label: t.label, rate: t.rate, amount: 0 })), country_code: code, tax_rate: 0 })); }} className="input text-[13px] py-2" data-testid="est-tax-country">
                  <option value="">Manual tax</option>
                  <option value="US">US (Sales Tax)</option><option value="UK">UK (VAT 20%)</option><option value="EU">EU (VAT 21%)</option>
                  <option value="IN">India (GST)</option><option value="CA">Canada (GST+PST)</option><option value="AU">Australia (10%)</option>
                </select>
              </div>

              {estimate.taxes?.length > 0 ? (
                <div className="space-y-2">
                  {estimate.taxes.map((t, i) => (
                    <div key={i} className="flex items-center justify-between gap-2">
                      <input type="text" value={t.label} onChange={e => { const taxes = [...estimate.taxes]; taxes[i] = { ...taxes[i], label: e.target.value }; setEstimate(prev => ({ ...prev, taxes })); }} className="w-20 input text-[12px] py-1.5 px-2" />
                      <div className="flex items-center gap-1">
                        <input type="number" value={t.rate} onChange={e => { const taxes = [...estimate.taxes]; taxes[i] = { ...taxes[i], rate: parseFloat(e.target.value) || 0 }; setEstimate(prev => ({ ...prev, taxes })); }} className="w-16 input text-right font-mono text-[12px] py-1.5 px-2" min="0" step="0.1" />
                        <span className="text-[12px] text-[#6B7280]">%</span>
                      </div>
                      <span className="font-mono text-[12px] w-16 text-right">{formatCurrency(subtotal * (t.rate / 100))}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-between gap-4">
                  <span className="text-[14px] text-[#6B7280]">Tax (%)</span>
                  <input type="number" value={estimate.tax_rate} onChange={e => setEstimate(prev => ({ ...prev, tax_rate: parseFloat(e.target.value) || 0 }))} className="w-20 input text-right font-mono text-[13px] py-2 px-3" min="0" step="0.1" data-testid="est-tax-rate" />
                </div>
              )}

              <div className="flex items-center justify-between gap-4">
                <span className="text-[14px] text-[#6B7280]">Discount</span>
                <input type="number" value={estimate.discount} onChange={e => setEstimate(prev => ({ ...prev, discount: parseFloat(e.target.value) || 0 }))} className="w-20 input text-right font-mono text-[13px] py-2 px-3" min="0" step="0.01" data-testid="est-discount" />
              </div>

              <div className="flex justify-between pt-3 border-t border-[#0A0A0A]">
                <span className="font-semibold text-[15px]">Total</span>
                <span className="font-display text-[24px] tracking-[-0.02em] text-[#0A0A0A]" data-testid="est-total">{formatCurrency(total)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
};

export default EstimateBuilder;
